import { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { ModalShell } from '../../../ui';
import { apService } from '../../../../services/apService';
import { useConfirm } from '../../../../contexts/ConfirmContext';
import { useInstitutionConfig } from '../../../../contexts/InstitutionConfigContext';
import APStatusSummary, { type APStatus } from './APStatusSummary';
import APScoreForm, { type APExam, type APUserScore } from './APScoreForm';
import APScoresList, { type APScore } from './APScoresList';
import APChoiceDialog, { type APChoiceInfo } from './APChoiceDialog';

export interface APEvaluationResult {
  total_credits: number;
  [key: string]: unknown;
}

interface ToastOptions {
  title?: string;
  subMessage?: string;
  duration?: number;
  [key: string]: unknown;
}

type ToastFn = (message: string, options?: ToastOptions) => void;

export interface APCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  onSave?: (result: APEvaluationResult) => Promise<void> | void;
  onRefreshPlan?: () => Promise<void> | void;
  showSuccess?: ToastFn;
  showError?: ToastFn;
  /** Currently unused; accepted so callers can pass the toast trio uniformly. */
  showWarning?: ToastFn;
}

interface PendingAdd {
  examId: number;
  score: number;
  year: number | null;
}

const APCreditsModal = ({
  isOpen,
  onClose,
  userId,
  onSave,
  onRefreshPlan,
  showSuccess,
  showError,
}: APCreditsModalProps) => {
  const [apExams, setAPExams] = useState<APExam[]>([]);
  const [userScores, setUserScores] = useState<(APUserScore & APScore)[]>([]);
  const [apStatus, setAPStatus] = useState<APStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [choiceInfo, setChoiceInfo] = useState<APChoiceInfo | null>(null);
  const [pendingAdd, setPendingAdd] = useState<PendingAdd | null>(null);

  const [formKey, setFormKey] = useState(0);

  const { confirm } = useConfirm();
  // Per-institution PLC cap (UMD 60, SJSU 30) — UX-R1 task 1.1.
  const { config } = useInstitutionConfig();
  const plcCap = config.priorLearningCap;

  useEffect(() => {
    if (isOpen && userId) {
      loadAPData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const loadAPData = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const [exams, scores, status] = await Promise.all([
        apService.getAPExams(),
        apService.getUserAPScores(),
        apService.getAPStatus(),
      ]);

      setAPExams(exams as APExam[]);
      setUserScores(scores as (APUserScore & APScore)[]);
      setAPStatus(status as APStatus);
    } catch (err) {
      console.error('Error loading AP data:', err);
      setError((err as { message?: string })?.message ?? 'Failed to load AP data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddScore = async (
    examId: number,
    score: number,
    year: number | null
  ): Promise<void> => {
    const existingScore = userScores.find((s) => s.ap_exam_id === examId);
    if (existingScore) {
      showError?.(
        'You already have a score for this exam. Remove the existing score first.'
      );
      return;
    }

    const exam = apExams.find((e) => e.id === examId);
    if (!exam) {
      showError?.('Exam not found');
      return;
    }

    setSaving(true);
    try {
      const choiceResult = (await apService.checkAPChoice(exam.name, score)) as {
        has_choice?: boolean;
        choice_info?: APChoiceInfo;
      };

      if (choiceResult.has_choice && choiceResult.choice_info) {
        setPendingAdd({ examId, score, year });
        setChoiceInfo(choiceResult.choice_info);
        setShowChoiceDialog(true);
        setSaving(false);
        return;
      }

      await submitAPScore(examId, score, year, null);
    } catch (err) {
      console.error('Error adding AP score:', err);
      showError?.(`Failed to add AP score: ${(err as { message?: string })?.message ?? ''}`);
      setSaving(false);
    }
  };

  const submitAPScore = async (
    examId: number,
    score: number,
    year: number | null,
    courseChoice: string | null
  ): Promise<void> => {
    setSaving(true);
    try {
      await apService.addAPScore(examId, score, year, courseChoice);
      await loadAPData();

      setShowChoiceDialog(false);
      setChoiceInfo(null);
      setPendingAdd(null);
      setFormKey((k) => k + 1);

      showSuccess?.('AP score added successfully');
    } catch (err) {
      console.error('Error adding AP score:', err);
      showError?.(`Failed to add AP score: ${(err as { message?: string })?.message ?? ''}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChoiceConfirm = (selectedChoice: string): void => {
    if (pendingAdd) {
      submitAPScore(pendingAdd.examId, pendingAdd.score, pendingAdd.year, selectedChoice);
    }
  };

  const handleChoiceCancel = (): void => {
    setShowChoiceDialog(false);
    setChoiceInfo(null);
    setPendingAdd(null);
  };

  const handleRemoveScore = async (
    scoreId: APScore['id'],
    examName: string
  ): Promise<void> => {
    const confirmed = await confirm({
      title: 'Remove AP Score',
      message: `Remove your ${examName} AP score? This will also remove any generated course credits.`,
      confirmText: 'Remove',
      variant: 'danger',
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      await apService.removeAPScore(Number(scoreId));
      await loadAPData();
      showSuccess?.(`${examName} AP score removed successfully`);
    } catch (err) {
      console.error('Error removing AP score:', err);
      showError?.(
        `Failed to remove AP score: ${(err as { message?: string })?.message ?? ''}`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const evalResult = (await apService.evaluateAPCredits()) as Partial<APEvaluationResult>;
      const result: APEvaluationResult = {
        ...evalResult,
        total_credits: evalResult.total_credits ?? 0,
      };
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (onRefreshPlan) {
        await onRefreshPlan();
      }

      onClose();

      if (result.total_credits > 0) {
        showSuccess?.(`AP credits saved! ${result.total_credits} credits awarded.`, {
          title: 'AP Credits Updated',
          subMessage: 'New courses added to Transfer Credits section.',
          duration: 6000,
        });
      } else {
        showSuccess?.('AP credits updated successfully.');
      }

      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      console.error('Error saving AP credits:', err);
      showError?.(`Failed to save AP credits: ${(err as { message?: string })?.message ?? ''}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel="Advanced Placement credits"
      maxWidth="max-w-4xl"
      className="max-h-[90vh] flex flex-col"
    >
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Advanced Placement Credits</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage your AP exam scores and view awarded course credits
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
              disabled={saving}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-600">Loading AP data...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12 px-6">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={loadAPData}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                <APStatusSummary apStatus={apStatus} />

                <APScoreForm
                  key={formKey}
                  apExams={apExams}
                  userScores={userScores}
                  saving={saving}
                  onAddScore={handleAddScore}
                />

                <APScoresList
                  scores={userScores}
                  saving={saving}
                  onRemoveScore={handleRemoveScore}
                />

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">How AP Credits Work</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Only scores of 3, 4, or 5 can earn course credit</li>
                    <li>• Credits appear in your Transfer Credits section after saving</li>
                    <li>• Maximum {plcCap} Prior Learning Credits allowed (includes AP, IB, etc.)</li>
                    <li>• Some exams have exclusion rules (e.g., Calculus AB vs BC)</li>
                    <li>• Language exams require advisor consultation for final approval</li>
                  </ul>
                </div>

                <APChoiceDialog
                  isOpen={showChoiceDialog}
                  choiceInfo={choiceInfo}
                  saving={saving}
                  onConfirm={handleChoiceConfirm}
                  onCancel={handleChoiceCancel}
                />
              </div>
            )}
          </div>

          <div className="flex-shrink-0 border-t bg-gray-50">
            {!loading && !error && (
              <div className="flex items-center justify-end space-x-2 px-6 py-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    'Save AP Credits'
                  )}
                </button>
              </div>
            )}
          </div>
    </ModalShell>
  );
};

export default APCreditsModal;
